import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  CardActions,
  CardContent,
  Typography,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogContent,
  Autocomplete,
  TextField,
  Snackbar,
  Alert,
  Paper,
  InputAdornment,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddDiplomaTemplateDialog from './addDiplomaTemplateDialog';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { API_BASE_URL } from 'src/apiConfig';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import { Edit, Delete } from '@mui/icons-material';
import ClassIcon from '@mui/icons-material/Class';
import { cyrillicToLatin } from 'src/helper/helperFunction';
import { useTranslation } from 'react-i18next';


const PageWrapper = styled(Box)({ padding: '24px' });

const HeaderPaper = styled(Paper)({
  padding: '24px 28px',
  borderRadius: '16px',
  marginBottom: '20px',
  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #42a5f5 100%)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: '0 4px 20px rgba(21,101,192,0.3)',
  flexWrap: 'wrap',
  gap: '12px',
});

const AddButton = styled(Button)({
  backgroundColor: 'rgba(255,255,255,0.95)',
  color: '#1565c0',
  fontWeight: 700,
  borderRadius: '10px',
  textTransform: 'none',
  fontSize: '14px',
  padding: '8px 20px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  '&:hover': { backgroundColor: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,0.2)' },
});

const ContentPaper = styled(Paper)({
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  padding: '20px',
  backgroundColor: '#fff',
});

const FilterBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '20px',
  flexWrap: 'wrap',
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#f8faff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
  },
});

const TemplateCard = styled(Card)({
  borderRadius: '16px',
  width: '360px',
  backgroundColor: '#fff',
  border: '1px solid #e8edf5',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(21,101,192,0.15)',
  },
});


const DiplomaTemplates = () => {
  const { t } = useTranslation();

  const diplomaTypes = [
    { id: 1, label: t('diplomaTemplate.zlatna') },
    { id: 2, label: t('diplomaTemplate.prviRang') },
    { id: 3, label: t('diplomaTemplate.finalniRang') },
    { id: 4, label: t('diplomaTemplate.zahvalnica') },
  ];

  const TYPE_CHIPS = {
    1: { bg: '#fff8e1', color: '#f57f17', border: '#ffe082', badge: 'ZZ' },
    2: { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9', badge: 'PR' },
    3: { bg: '#f3e5f5', color: '#6a1b9a', border: '#ce93d8', badge: 'F' },
    4: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7', badge: 'Z' },
  };

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ name: '', type: '', year: '', image: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [imageZoom, setImageZoom] = useState({ open: false, src: '' });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/diplomaTemplates.php`);
      setTemplates(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEditTemplate = async () => {
    try {
      if (editingTemplate) {
        const formData = new FormData();
        formData.append('name', newTemplate.name);
        formData.append('type', Number(newTemplate.type));
        formData.append('year', Number(newTemplate.year));
        formData.append('existingImage', editingTemplate.image);
        if (newTemplate.image instanceof File) formData.append('image', newTemplate.image);

        const response = await axios.post(
          `${API_BASE_URL}/api/diplomaTemplates.php?id=${editingTemplate.id}&_method=PUT`,
          formData
        );
        setTemplates((prev) =>
          prev.map((item) => item.id === editingTemplate.id ? response.data.data : item)
        );
        setSnackbarMessage(response?.data.success);
        setSnackbarOpen(true);
      } else {
        const formData = new FormData();
        formData.append('name', newTemplate.name);
        formData.append('type', newTemplate.type);
        formData.append('year', newTemplate.year);
        if (newTemplate.image instanceof File) formData.append('image', newTemplate.image);

        const response = await axios.post(
          `${API_BASE_URL}/api/diplomaTemplates.php`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        const created = response?.data?.data;
        if (created && created.id) {
          setTemplates((prev) => [...prev, created]);
        } else {
          setTemplates((prev) => [...prev, {
            id: Date.now(),
            name: newTemplate.name,
            type: newTemplate.type,
            year: newTemplate.year,
            image: newTemplate.image instanceof File
              ? URL.createObjectURL(newTemplate.image)
              : newTemplate.image ? `${API_BASE_URL}/api/${newTemplate.image}` : null,
          }]);
        }
        setSnackbarMessage(response?.data.success);
        setSnackbarOpen(true);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving diploma template:', error);
      handleCloseDialog();
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      if (templateToDelete) {
        const response = await axios.delete(
          `${API_BASE_URL}/api/diplomaTemplates.php?id=${templateToDelete}`,
          { data: { id: templateToDelete } }
        );
        setTemplates((prev) => prev.filter((t) => t.id !== templateToDelete));
        handleCloseDeleteDialog();
        setSnackbarMessage(response?.data.success);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error deleting diploma template:', error);
      setTemplates((prev) => prev.filter((t) => t.id !== templateToDelete));
      handleCloseDeleteDialog();
    }
  };

  const handleOpenDeleteDialog = (id) => {
    setTemplateToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setTemplateToDelete(null);
  };

  const handleOpenDialog = (template = null) => {
    setEditingTemplate(template);
    if (template) {
      setNewTemplate({
        name: template.name || '',
        type: String(template.type) || '',
        year: String(template.year) || '',
        image: template.image || null,
        id: template.id,
      });
    } else {
      setNewTemplate({ name: '', type: '', year: '', image: null });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setNewTemplate({ name: '', type: '', year: '', image: null });
  };

  const getTypeLabel = (type) =>
    diplomaTypes.find((t) => String(t.id) === String(type))?.label || type;

  const normalizedSearchTerm = cyrillicToLatin(searchTerm.toLowerCase());
  const availableYears = ['All', ...Array.from(new Set(templates.map((t) => String(t.year))))];

  const filteredTemplates = templates
    .filter((t) => selectedYear === 'All' || String(t.year) === String(selectedYear))
    .filter((t) => selectedType === 'All' || String(t.type) === String(selectedType))
    .filter((template) =>
      template.name && cyrillicToLatin(template.name.toLowerCase()).includes(normalizedSearchTerm)
    );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageWrapper>
      <HeaderPaper elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ClassIcon sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {t('diplomaTemplate.pageTitle')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {t('diplomaTemplate.searchPlaceholder')}
            </Typography>
          </Box>
        </Box>
        <AddButton startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          {t('diplomaTemplate.addNew')}
        </AddButton>
      </HeaderPaper>

      <ContentPaper>
        <FilterBar>
          <StyledTextField
            label={t('diplomaTemplate.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: '240px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 140 }}
            options={availableYears.map(String)}
            value={selectedYear}
            onChange={(_, newValue) => setSelectedYear(newValue || 'All')}
            getOptionLabel={(o) => o === 'All' ? t('general.all') : o.toString()}
            renderInput={(params) => (
              <StyledTextField {...params} label={t('diplomaTemplate.year')} />
            )}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 260 }}
            options={[{ id: 'All', label: t('general.all') }, ...diplomaTypes]}
            getOptionLabel={(option) => option.label}
            value={
              [{ id: 'All', label: t('general.all') }, ...diplomaTypes]
                .find((opt) => String(opt.id) === String(selectedType)) || null
            }
            onChange={(_, newValue) => setSelectedType(newValue ? newValue.id : 'All')}
            renderInput={(params) => (
              <StyledTextField {...params} label={t('diplomaTemplate.type')} />
            )}
          />
        </FilterBar>

        {filteredTemplates.length === 0 ? (
          <Box sx={{
            textAlign: 'center', py: 8,
            color: '#aaa',
            border: '2px dashed #e0e0e0',
            borderRadius: '12px',
          }}>
            <ClassIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography variant="body1">{t('diplomaTemplate.noTemplates')}</Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {filteredTemplates.map((template) => {
              const chipCfg = TYPE_CHIPS[String(template.type)] || { bg: '#f5f5f5', color: '#555', border: '#ddd', badge: '?' };
              return (
                <Grid item key={template.id}>
                  <TemplateCard>
                    <CardContent sx={{ p: 2, pb: 1 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box
                          component="img"
                          src={template.image ? `${API_BASE_URL}/api/${template.image}` : '/kengur2/assets/kengur.png'}
                          alt="template"
                          onClick={() => setImageZoom({
                            open: true,
                            src: template.image ? `${API_BASE_URL}/api/${template.image}` : '/kengur2/assets/kengur.png',
                          })}
                          sx={{
                            width: 90, height: 90,
                            objectFit: 'cover',
                            borderRadius: '10px',
                            border: '1px solid #e8edf5',
                            cursor: 'pointer',
                            flexShrink: 0,
                            transition: 'opacity 0.2s',
                            '&:hover': { opacity: 0.85 },
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: '#1a237e',
                              fontWeight: 700,
                              fontSize: '15px',
                              lineHeight: 1.3,
                              mb: 1,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {template.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                            <Box sx={{
                              px: 0.9, py: 0.1,
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: 800,
                              lineHeight: '20px',
                              backgroundColor: chipCfg.bg,
                              color: chipCfg.color,
                              border: `1px solid ${chipCfg.border}`,
                              userSelect: 'none',
                            }}>
                              {chipCfg.badge}
                            </Box>
                            <Chip
                              label={getTypeLabel(template.type)}
                              size="small"
                              sx={{
                                backgroundColor: chipCfg.bg,
                                color: chipCfg.color,
                                border: `1px solid ${chipCfg.border}`,
                                fontWeight: 700,
                                fontSize: '11px',
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
                            {t('diplomaTemplate.yearLabel')} <strong>{template.year || 'N/A'}</strong>
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ px: 2, pb: 1.5, pt: 0.5, borderTop: '1px solid #f0f4fb' }}>
                      <Button
                        size="small"
                        startIcon={<Edit sx={{ fontSize: '16px !important' }} />}
                        onClick={() => handleOpenDialog(template)}
                        sx={{
                          color: '#1976d2',
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: '8px',
                          fontSize: '13px',
                          '&:hover': { backgroundColor: '#e3f2fd' },
                        }}
                      >
                        {t('dialog.edit')}
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Delete sx={{ fontSize: '16px !important' }} />}
                        onClick={() => handleOpenDeleteDialog(template.id)}
                        sx={{
                          color: '#e91e63',
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: '8px',
                          fontSize: '13px',
                          '&:hover': { backgroundColor: '#fce4ec' },
                        }}
                      >
                        {t('dialog.delete')}
                      </Button>
                    </CardActions>
                  </TemplateCard>
                </Grid>
              );
            })}
          </Grid>
        )}
      </ContentPaper>

      <AddDiplomaTemplateDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleAddOrEditTemplate}
        template={newTemplate}
        setTemplate={setNewTemplate}
        diplomaTypes={diplomaTypes}
      />

      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteTemplate}
      />

      {/* Image zoom */}
      <Dialog
        open={imageZoom.open}
        onClose={() => setImageZoom({ open: false, src: '' })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogContent sx={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          minHeight: '400px', maxHeight: '80vh', overflow: 'hidden', p: 1,
          backgroundColor: '#0a0a0a',
        }}>
          <img
            src={imageZoom.src}
            alt={t('diplomaTemplate.pageTitle')}
            style={{ maxWidth: '100%', maxHeight: '75vh', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </PageWrapper>
  );
};

export default DiplomaTemplates;